import React, { Fragment, useEffect, useState } from "react";
import { OidcSecureChat } from "./OidcSecureChat";
import { initializePage } from "./functions/initializePage";
import { Loading } from "./Loading";
import { CssBaseline } from "@mui/material";
import { BrowserRouter, Route } from "react-router-dom";
import { ApmRoutes } from "@elastic/apm-rum-react";
import LoginPage from "./LoginPage";
import AuthorizationErrorPage from "./components/AuthorizationErrorPage";
import { AuthorizationErrorProvider } from "./contexts/AuthorizationErrorContext";
import AuthorizationErrorBoundary from "./components/AuthorizationErrorBoundary";
import { useAvailableModels } from "./hooks/useAvailableModels";
import { useOidcConfigList } from "./hooks/useOidcConfigList";
import { BetaBanner } from "./components/BetaBanner";
import { redirectToOpenWebUIIfEnabled, isOpenWebUIAvailable } from "./functions/betaExperience";
import { useGlobalOptions } from "./hooks/useGlobalOptions";

export const AppContainer = () => {
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(false);
  const availableModelsContext = useAvailableModels();
  const oidcConfigListContext = useOidcConfigList();
  const { globalOptions, loading } = useGlobalOptions();

  useEffect(() => {
    initializePage();
    
    // Check if we should redirect to OpenWebUI immediately
    // This handles the case where user refreshes the page with beta enabled
    // Only check after global options are loaded
    if (!loading && isOpenWebUIAvailable(globalOptions.openwebui_url)) {
      redirectToOpenWebUIIfEnabled(globalOptions.openwebui_url);
    }
  }, [globalOptions.openwebui_url, loading]);

  const handleBannerDismiss = () => {
    setBannerDismissed(true);
  };

  const bannerVisible = !bannerDismissed && isOpenWebUIAvailable(globalOptions.openwebui_url);

  return (
    <Loading contexts={[availableModelsContext, oidcConfigListContext]}>
      <Fragment>
        <CssBaseline />
        {/* Beta Banner - only show if not dismissed and OpenWebUI is available */}
        {bannerVisible && (
          <BetaBanner onDismiss={handleBannerDismiss} />
        )}
        <AuthorizationErrorProvider>
          <BrowserRouter>
            <AuthorizationErrorBoundary>
              <ApmRoutes>
                <Route path="/" element={<OidcSecureChat betaBannerVisible={bannerVisible} />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/unauthorized" element={<AuthorizationErrorPage />} />
              </ApmRoutes>
            </AuthorizationErrorBoundary>
          </BrowserRouter>
        </AuthorizationErrorProvider>
      </Fragment>
    </Loading>
  );
};
